/**
 * @file useEntitySearchResults.ts
 * @brief Debounced entity search for the Cmd+K palette (role-aware).
 *
 * @details
 * Reuses existing list/options hooks. Fires only when the query is ≥ 2
 * chars and the palette is open. Employees have no server search today —
 * filtered client-side from `useEmployeesList`. Meeting hits use
 * `buildMeetingPaletteItem` (participants, owner/company, overview keywords).
 *
 * @references
 * - features/companies/api.ts (useCompanyOptions)
 * - features/users/api.ts (useUserOptions)
 * - features/admin-meetings/api.ts
 * - features/company-meetings/api.ts
 * - features/command-palette/meetingPaletteUtils.ts
 *
 * @author DES
 * @date 17-07-2026
 * @updated 30-07-2026 — richer Meeting Intel entity rows + keywords
 */

import { useMemo } from "react";
import {
  Building2,
  Receipt,
  Users,
} from "lucide-react";
import { SessionTurns } from "@/components/icons/SessionTurns";
import { useCompanyOptions } from "@/features/companies/api";
import { useUserOptions } from "@/features/users/api";
import { useEmployeesList } from "@/features/employees/api";
import { useAdminMeetingsList } from "@/features/admin-meetings/api";
import { useCompanyMeetingsList } from "@/features/company-meetings/api";
import { useSessionsList } from "@/features/sessions/api";
import { useCompanySessionsList } from "@/features/company-sessions/api";
import { useAdminInvoicesList } from "@/features/admin-invoices/api";
import { useCompanyInvoicesList } from "@/features/company-invoices/api";
import { useNotesList } from "@/features/notes/api";
import { useMyTodos } from "@/features/todos/myTodosApi";
import { MeetingNotesPen } from "@/components/icons/MeetingNotesPen";
import { TodoTick } from "@/components/icons/TodoTick";
import { useDebouncedValue } from "@/shared/hooks/useDebouncedValue";
import type { CommandPaletteItem, PaletteScope } from "./commandPaletteTypes";
import { buildMeetingPaletteItem } from "./meetingPaletteUtils";

const RESULT_LIMIT = 8;
const MIN_QUERY = 2;
const NO_USER_MODULES = { meetings: false, notes: false, todos: false };

function personLabel(
  first?: string | null,
  last?: string | null,
  email?: string | null,
): string {
  const name = [first, last].filter(Boolean).join(" ").trim();
  if (name) return name;
  return email?.trim() || "Unknown";
}

export function useEntitySearchResults(options: {
  open: boolean;
  query: string;
  scope: PaletteScope;
  /** A normal user's searchable modules, from their own menu (21-10-2026). */
  userModules?: { meetings: boolean; notes: boolean; todos: boolean };
}): CommandPaletteItem[] {
  const { open, query, scope } = options;
  const isSuperAdmin = scope === "superadmin";
  const isCompany = scope === "company";
  const mods = scope === "user" ? options.userModules ?? NO_USER_MODULES : null;
  const debounced = useDebouncedValue(query.trim(), 300);
  const active = open && debounced.length >= MIN_QUERY;

  const companies = useCompanyOptions(debounced, active && isSuperAdmin);
  const users = useUserOptions(debounced, active && isSuperAdmin);
  const employees = useEmployeesList(active && isCompany);
  const adminMeetings = useAdminMeetingsList(
    { search: debounced, page: 1, pageSize: RESULT_LIMIT },
    { enabled: active && isSuperAdmin },
  );
  // The boardroom list is scoped by the server to what the viewer may read,
  // so a normal user gets their own (and shared) meetings from the same call.
  const companyMeetings = useCompanyMeetingsList(
    { search: debounced, page: 1, pageSize: RESULT_LIMIT },
    { enabled: active && (isCompany || !!mods?.meetings) },
  );
  const adminSessions = useSessionsList(
    { search: debounced, page: 1, limit: RESULT_LIMIT },
    { enabled: active && isSuperAdmin },
  );
  const companySessions = useCompanySessionsList(
    { q: debounced, page: 1, limit: RESULT_LIMIT },
    { enabled: active && isCompany },
  );
  const adminInvoices = useAdminInvoicesList(
    { q: debounced, page: 1, pageSize: RESULT_LIMIT },
    { enabled: active && isSuperAdmin },
  );
  const companyInvoices = useCompanyInvoicesList(
    { q: debounced, page: 1, pageSize: RESULT_LIMIT },
    { enabled: active && isCompany },
  );
  // A normal user's notes and to-dos: the same server searches their pages use.
  const myNotes = useNotesList({ search: debounced, page: 1, pageSize: RESULT_LIMIT }, active && !!mods?.notes);
  const myTodos = useMyTodos({ q: debounced, limit: RESULT_LIMIT }, active && !!mods?.todos);

  return useMemo(() => {
    if (!active) return [];

    const items: CommandPaletteItem[] = [];
    const q = debounced.toLowerCase();

    if (isSuperAdmin) {
      for (const c of (companies.data?.companies ?? []).slice(0, RESULT_LIMIT)) {
        items.push({
          id: `company:${c.id}`,
          kind: "company",
          label: c.name,
          description: c.domain || c.billing_email || "Company",
          keywords: `${c.name} ${c.domain ?? ""} ${c.billing_email ?? ""}`,
          icon: Building2,
          to: `/admin/companies?q=${encodeURIComponent(c.name)}`,
        });
      }

      for (const u of (users.data?.users ?? []).slice(0, RESULT_LIMIT)) {
        const label = personLabel(u.first_name, u.last_name, u.email);
        items.push({
          id: `user:${u.id}`,
          kind: "user",
          label,
          description: [u.email, u.company_name || u.company]
            .filter(Boolean)
            .join(" · "),
          keywords: `${label} ${u.email} ${u.company_name ?? ""}`,
          icon: Users,
          to: `/admin/users?q=${encodeURIComponent(u.email)}`,
        });
      }

      for (const m of (adminMeetings.data?.meetings ?? []).slice(
        0,
        RESULT_LIMIT,
      )) {
        items.push(buildMeetingPaletteItem(m, { isSuperAdmin: true }));
      }

      for (const s of (adminSessions.data?.sessions ?? []).slice(
        0,
        RESULT_LIMIT,
      )) {
        const label =
          s.reference_number ||
          [s.person_a_name, s.person_b_name].filter(Boolean).join(" ↔ ") ||
          s.session_id.slice(0, 8);
        items.push({
          id: `session:${s.session_id}`,
          kind: "session",
          label,
          description: [s.company_name, s.owner_email, s.status]
            .filter(Boolean)
            .join(" · "),
          keywords: `${label} ${s.session_id} ${s.owner_email ?? ""}`,
          icon: SessionTurns, // DES 08-10-2026: app icon, was Activity
          to: `/admin/sessions?q=${encodeURIComponent(s.reference_number || s.session_id)}`,
        });
      }

      for (const inv of (adminInvoices.data?.items ?? []).slice(
        0,
        RESULT_LIMIT,
      )) {
        const owner = inv.ownerName || inv.ownerEmail || "Invoice";
        items.push({
          id: `invoice:${inv.id}`,
          kind: "invoice",
          label: owner,
          description: [inv.companyName, inv.status, inv.product]
            .filter(Boolean)
            .join(" · "),
          keywords: `${owner} ${inv.ownerEmail ?? ""} ${inv.companyName ?? ""} invoice`,
          icon: Receipt,
          to: `/admin/invoices?q=${encodeURIComponent(inv.ownerEmail || inv.companyName || inv.id)}`,
        });
      }
    } else if (mods) {
      for (const m of (companyMeetings.data?.meetings ?? []).slice(0, RESULT_LIMIT)) {
        items.push(buildMeetingPaletteItem(m, { isSuperAdmin: false, base: "/app/meetings" }));
      }

      for (const n of (myNotes.data?.items ?? []).slice(0, RESULT_LIMIT)) {
        const label = n.title?.trim() || n.summary?.trim().split("\n")[0]?.slice(0, 80) || "Untitled note";
        items.push({
          id: `note:${n.id}`,
          kind: "note",
          label,
          description: n.summary?.trim() ? n.summary.trim().slice(0, 120) : "Note",
          keywords: `${label} ${n.summary ?? ""} note`,
          icon: MeetingNotesPen,
          to: `/app/notes/${n.id}`,
        });
      }

      const todoRows = (myTodos.data?.groups ?? []).flatMap((g) => g.items);
      for (const t of todoRows.slice(0, RESULT_LIMIT)) {
        const label = t.task?.trim() || "To-do";
        items.push({
          id: `todo:${t.meetingId}:${t.todoId}`,
          kind: "todo",
          label,
          description: [t.meetingTitle, t.completed ? "Done" : t.dueDate ? `Due ${t.dueDate}` : null].filter(Boolean).join(" · ") || "To-do",
          keywords: `${label} ${t.meetingTitle ?? ""} todo to-do task`,
          icon: TodoTick,
          // My to-dos, filtered to it: a "to-do only" share has no meeting
          // to open (12-10-2026).
          to: `/app/todos?q=${encodeURIComponent(label.slice(0, 60))}`,
        });
      }
    } else {
      const employeeMatches = (employees.data ?? [])
        .filter((e) => {
          const hay = `${e.first_name} ${e.last_name} ${e.email} ${e.title ?? ""}`.toLowerCase();
          return hay.includes(q);
        })
        .slice(0, RESULT_LIMIT);

      for (const e of employeeMatches) {
        const label = personLabel(e.first_name, e.last_name, e.email);
        items.push({
          id: `employee:${e.id}`,
          kind: "employee",
          label,
          description: [e.email, e.title].filter(Boolean).join(" · "),
          keywords: `${label} ${e.email}`,
          icon: Users,
          to: `/company/employees?q=${encodeURIComponent(e.email)}`,
        });
      }

      for (const m of (companyMeetings.data?.meetings ?? []).slice(
        0,
        RESULT_LIMIT,
      )) {
        items.push(buildMeetingPaletteItem(m, { isSuperAdmin: false }));
      }

      for (const s of (companySessions.data?.sessions ?? []).slice(
        0,
        RESULT_LIMIT,
      )) {
        const label =
          [s.person_a_name, s.person_b_name].filter(Boolean).join(" ↔ ") ||
          s.session_id.slice(0, 8);
        items.push({
          id: `session:${s.session_id}`,
          kind: "session",
          label,
          description: [s.status, s.participant_email].filter(Boolean).join(" · "),
          keywords: `${label} ${s.session_id}`,
          icon: SessionTurns, // DES 08-10-2026: app icon, was Activity
          to: `/company/sessions?q=${encodeURIComponent(s.session_id)}`,
        });
      }

      for (const inv of (companyInvoices.data?.items ?? []).slice(
        0,
        RESULT_LIMIT,
      )) {
        const owner = inv.ownerName || inv.ownerEmail || "Invoice";
        items.push({
          id: `invoice:${inv.id}`,
          kind: "invoice",
          label: owner,
          description: [inv.status].filter(Boolean).join(" · "),
          keywords: `${owner} ${inv.ownerEmail ?? ""} invoice`,
          icon: Receipt,
          to: `/company/invoices?q=${encodeURIComponent(inv.ownerEmail || inv.id)}`,
        });
      }
    }

    return items;
  }, [
    active,
    debounced,
    isSuperAdmin,
    companies.data,
    users.data,
    employees.data,
    adminMeetings.data,
    companyMeetings.data,
    adminSessions.data,
    companySessions.data,
    adminInvoices.data,
    companyInvoices.data,
    mods,
    myNotes.data,
    myTodos.data,
  ]);
}
